import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Services
export const auth = getAuth(app);
export const db = getFirestore(app);

/**
 * Développement local uniquement : `VITE_USE_EMULATORS=true` branche Auth, Firestore,
 * Functions et Storage sur les émulateurs (firebase emulators:start --project demo-atable),
 * pour parcourir le tableau de bord sans toucher à preprod ni à la prod. Jamais défini
 * dans .env / .env.preprod.local, ignoré hors du serveur de dev (`vite build` refuse le
 * drapeau) et seulement pour un projet fictif `demo-*` : jamais preprod ni prod.
 */
export const USE_EMULATORS = import.meta.env.DEV
    && import.meta.env.VITE_USE_EMULATORS === 'true'
    && String(firebaseConfig.projectId ?? '').startsWith('demo-');
if (USE_EMULATORS) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    // Même instance que firestoreService / PendingRedemptions (getFunctions(app) est mémorisé).
    connectFunctionsEmulator(getFunctions(app), '127.0.0.1', 5001);
}

// Analytics (optionnel, seulement en production)
let analytics: ReturnType<typeof getAnalytics> | null = null;
try {
    // Ni en mode démo / tests (pas de données Firebase), ni sans identifiant de mesure.
    if (typeof window !== 'undefined' && !USE_EMULATORS
        && import.meta.env.VITE_USE_FIREBASE === 'true' && firebaseConfig.measurementId) {
        analytics = getAnalytics(app);
    }
} catch (e) {
    // Analytics non disponible en dev
}
export { analytics };

/**
 * Firebase Storage (photos de la vitrine restaurant, cf. storage.rules).
 * Chargé à la demande : seul l'éditeur « Mon établissement » en a besoin,
 * inutile d'alourdir le bundle principal.
 */
let storageEmulatorConnected = false;
export async function loadStorage() {
    const storageSdk = await import('firebase/storage');
    const storage = storageSdk.getStorage(app);
    if (USE_EMULATORS && !storageEmulatorConnected) {
        storageSdk.connectStorageEmulator(storage, '127.0.0.1', 9199);
        storageEmulatorConnected = true;
    }
    // Par défaut le SDK réessaie jusqu'à 10 min sans erreur : au-delà d'1 min
    // (envoi) ou 30 s (autres opérations), mieux vaut dire au restaurateur de réessayer.
    storage.maxUploadRetryTime = 60_000;
    storage.maxOperationRetryTime = 30_000;
    return { sdk: storageSdk, storage };
}

// FCM Messaging (optionnel — nécessite VITE_FIREBASE_VAPID_KEY)
let messaging: Messaging | null = null;
try {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        messaging = getMessaging(app);
    }
} catch (e) {
    // FCM non disponible (config manquante, navigateur non supporté)
}
export { messaging };

/**
 * Demande la permission de notifications et retourne le token FCM.
 * Retourne null si non disponible ou refusé.
 */
export async function requestFcmToken(): Promise<string | null> {
    if (!messaging) return null;
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) return null;

    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return null;

        const token = await getToken(messaging, { vapidKey });
        return token || null;
    } catch (e) {
        console.warn('FCM token request failed:', e);
        return null;
    }
}

export { onMessage };
export default app;
